/**
 * iter328 — Reclassement de l'impôt préalable non déductible (Comptabilité → TVA).
 *
 * Sous le régime TDFN, la TVA payée sur les achats n'est jamais récupérable : elle
 * doit rejoindre le compte de charge, de stock ou d'immobilisation d'origine.
 * Simulation d'abord, rien n'est écrit tant que l'exécution n'est pas confirmée.
 */
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Receipt, Play, Eye, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const fmt = (n) => (n || 0).toLocaleString("fr-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const IpReclassCard = ({ role }) => {
  const [sim, setSim] = useState(null);
  const [busy, setBusy] = useState(false);

  const simuler = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/tva/ip/simulation");
      setSim(data);
      toast.success("Rapport calculé — aucune écriture n'a été passée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const executer = async () => {
    if (!window.confirm(
      `Reclasser ${fmt(sim?.total_a_reclasser)} CHF d'impôt préalable sur les comptes d'origine ?\n\n`
      + "Aucune écriture existante n'est modifiée : seules des écritures de reclassement sont ajoutées.",
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/tva/ip/executer", { confirmer: true });
      toast.success(`${data.nb_ecritures} écriture(s) de reclassement · ${fmt(data.total)} CHF`);
      await simuler();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-4 sm:p-5" data-testid="ip-reclass-card">
      <div className="flex items-center gap-2 mb-1">
        <Receipt className="w-4 h-4 text-[#52a675]" />
        <h3 className="text-base font-semibold text-gray-900">Impôt préalable non déductible</h3>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        En régime TDFN, la TVA payée sur les achats n&apos;est pas récupérable : elle doit rejoindre
        le compte d&apos;origine de chaque pièce. Les écritures existantes ne sont jamais modifiées.
      </p>

      <div className="flex gap-2 flex-wrap">
        <Button size="sm" variant="outline" onClick={simuler} disabled={busy} data-testid="ip-simuler">
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Eye className="w-3.5 h-3.5 mr-1.5" />}
          Rapport de contrôle
        </Button>
        {role === "admin" && sim?.nb_ecritures > 0 && (
          <Button size="sm" onClick={executer} disabled={busy} data-testid="ip-executer">
            <Play className="w-3.5 h-3.5 mr-1.5" /> Reclasser
          </Button>
        )}
      </div>

      {sim && (
        <div className="mt-3 space-y-3" data-testid="ip-rapport">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            {[
              ["Solde 1170", sim.soldes_avant?.["1170"]?.solde],
              ["Solde 1171", sim.soldes_avant?.["1171"]?.solde],
              ["À reclasser", sim.total_a_reclasser],
              ["Solde après", sim.solde_ip_apres],
            ].map(([label, valeur]) => (
              <div key={label} className="rounded-lg border border-gray-200 px-2.5 py-2">
                <div className="text-[10px] uppercase tracking-wider text-gray-400">{label}</div>
                <div className="font-mono font-semibold text-gray-900">{fmt(valeur)}</div>
              </div>
            ))}
          </div>

          {sim.par_compte_destination?.length > 0 && (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-gray-400 text-left">
                  <th className="py-1">Compte de destination</th>
                  <th className="py-1 text-right">Pièces</th>
                  <th className="py-1 text-right">Montant</th>
                </tr>
              </thead>
              <tbody>
                {sim.par_compte_destination.slice(0, 12).map((c) => (
                  <tr key={c.compte} className="border-t border-gray-100" data-testid={`ip-compte-${c.compte}`}>
                    <td className="py-1.5">
                      <span className="font-mono">{c.compte}</span> <span className="text-gray-500">{c.libelle}</span>
                    </td>
                    <td className="py-1.5 text-right font-mono text-gray-500">{c.nb}</td>
                    <td className="py-1.5 text-right font-mono font-semibold">{fmt(c.montant)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {sim.nb_anomalies > 0 && (
            <div className="flex items-start gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>
                {sim.nb_anomalies} pièce(s) pour {fmt(sim.total_anomalies)} CHF sans compte d&apos;origine
                identifiable (ou sur exercice clôturé) : elles restent au 1170/1171 et expliquent le solde
                résiduel. À traiter manuellement avec la fiduciaire.
              </span>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default IpReclassCard;
